// src/config/routes.ts
import { colorConfig } from './colors';

export type SupportedLanguage = 'en' | 'es' | 'fr' | 'de' | 'it' | 'pl' | 'pt' | 'pt-BR' | 'nl' | 'ja';

export const LANGUAGE_STORAGE_KEY = 'preferredLanguage';

export const SUPPORTED_LANGUAGES = {
  en: { 
    name: 'English',
    nativeName: 'English',
    title: 'White Screen Online - Full Screen Color Tool',
    description: 'Create fullscreen color backgrounds for presentations, recording, or privacy. Free online tool for displaying solid colors in fullscreen mode.',
    region: 'US',
    dir: 'ltr',
    keywords: 'white screen, online tool, fullscreen color, presentation background, recording background'
  },
  es: { 
    name: 'Spanish',
    nativeName: 'Español',
    title: 'Pantalla Online - Herramienta de Color a Pantalla Completa',
    description: 'Crea fondos de color a pantalla completa para presentaciones, grabaciones o privacidad. Herramienta online gratuita.',
    region: 'ES',
    dir: 'ltr',
    keywords: 'pantalla blanca, herramienta online, color pantalla completa, fondo presentación'
  },
  fr: { 
    name: 'French',
    nativeName: 'Français',
    title: 'Écran en Ligne - Outil de Couleur Plein Écran',
    description: 'Créez des arrière-plans de couleur en plein écran pour les présentations, l\'enregistrement ou la confidentialité. Outil en ligne gratuit.',
    region: 'FR',
    dir: 'ltr',
    keywords: 'écran blanc, outil en ligne, couleur plein écran, arrière-plan présentation'
  },
  de: { 
    name: 'German',
    nativeName: 'Deutsch',
    title: 'Online Bildschirm - Vollbild-Farb-Tool',
    description: 'Erstellen Sie Vollbild-Farbhintergründe für Präsentationen, Aufnahmen oder Privatsphäre. Kostenloses Online-Tool.',
    region: 'DE',
    dir: 'ltr',
    keywords: 'weißer bildschirm, online-tool, vollbild-farbe, präsentationshintergrund'
  },
  it: { 
    name: 'Italian',
    nativeName: 'Italiano',
    title: 'Schermo Online - Strumento Colore a Schermo Intero',
    description: 'Crea sfondi colorati a schermo intero per presentazioni, registrazioni o privacy. Strumento online gratuito.',
    region: 'IT',
    dir: 'ltr',
    keywords: 'schermo bianco, strumento online, colore schermo intero, sfondo presentazione'
  },
  pl: { 
    name: 'Polish',
    nativeName: 'Polski',
    title: 'Ekran Online - Narzędzie Kolorów Pełnoekranowych',
    description: 'Twórz kolorowe tła pełnoekranowe do prezentacji, nagrywania lub prywatności. Darmowe narzędzie online.',
    region: 'PL',
    dir: 'ltr',
    keywords: 'biały ekran, narzędzie online, kolor pełnoekranowy, tło prezentacji'
  },
  pt: { 
    name: 'Portuguese',
    nativeName: 'Português',
    title: 'Tela Online - Ferramenta de Cor em Tela Cheia',
    description: 'Crie fundos coloridos em tela cheia para apresentações, gravações ou privacidade. Ferramenta online gratuita.',
    region: 'PT',
    dir: 'ltr',
    keywords: 'tela branca, ferramenta online, cor tela cheia, fundo apresentação'
  },
  'pt-BR': { 
    name: 'Brazilian Portuguese',
    nativeName: 'Português (Brasil)',
    title: 'Tela Online - Ferramenta de Cor em Tela Cheia',
    description: 'Crie fundos coloridos em tela cheia para apresentações, gravações ou privacidade. Ferramenta online gratuita.',
    region: 'BR',
    dir: 'ltr',
    keywords: 'tela branca, ferramenta online, cor tela cheia, fundo apresentação'
  },
  nl: { 
    name: 'Dutch',
    nativeName: 'Nederlands',
    title: 'Online Scherm - Volledig Scherm Kleurentool',
    description: 'Maak volledig scherm kleurachtergronden voor presentaties, opnames of privacy. Gratis online tool.',
    region: 'NL',
    dir: 'ltr',
    keywords: 'wit scherm, online tool, volledig scherm kleur, presentatie achtergrond'
  },
  ja: { 
    name: 'Japanese',
    nativeName: '日本語',
    title: '画面オンライン - フルスクリーンカラーツール',
    description: 'プレゼンテーション、録画、プライバシーのためのフルスクリーンカラー背景を作成。無料オンラインツール。',
    region: 'JP',
    dir: 'ltr',
    keywords: '白い画面, オンラインツール, フルスクリーンカラー, プレゼンテーション背景'
  }
} as const;

export const DEFAULT_LANGUAGE = 'en' as const;
export const BASE_URL = 'https://whitescreenonline.org' as const;

export const getUserPreferredLanguage = (): SupportedLanguage => {
  // Check localStorage first
  const storedLang = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (storedLang && isValidLanguage(storedLang)) {
    return storedLang;
  }

  // Check browser language
  const browserLang = navigator.language.split('-')[0];
  if (isValidLanguage(browserLang)) {
    return browserLang;
  }

  return DEFAULT_LANGUAGE;
};

export const createColorRoute = (lang: SupportedLanguage, colorKey: string): string => {
  const colorInfo = colorConfig[lang]?.[colorKey];
  if (!colorInfo) return `/${colorKey}-screen`;
  
  return lang === DEFAULT_LANGUAGE 
    ? `/${colorInfo.urlPath}`
    : `/${lang}/${colorInfo.urlPath}`;
};

export const getCanonicalUrl = (lang: SupportedLanguage, colorKey: string): string => {
  return `${BASE_URL}${createColorRoute(lang, colorKey)}`;
};

export const isValidLanguage = (lang: string): lang is SupportedLanguage => {
  return Object.keys(SUPPORTED_LANGUAGES).includes(lang);
};

export const getLanguageFromURL = (pathname: string): SupportedLanguage => {
  const langMatch = pathname.match(/^\/([a-z]{2}(?:-[A-Z]{2})?)/);
  const extractedLang = langMatch?.[1] ?? '';
  
  return isValidLanguage(extractedLang) ? extractedLang : getUserPreferredLanguage();
};

export const getColorFromURL = (pathname: string, lang: SupportedLanguage): string => {
  const pathParts = pathname.split('/');
  const lastPart = pathParts[pathParts.length - 1] || '';
  
  for (const [colorKey, colorInfo] of Object.entries(colorConfig[lang])) {
    if (colorInfo.urlPath === lastPart) {
      return colorKey;
    }
  }
  
  return 'white';
};